import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { RainbowButton } from '../ui/rainbow-button';
import { useLanguage } from '../../contexts/LanguageContext';

const Section = styled.section`
  width: 100%;
  background: linear-gradient(
    135deg,
    #1E5D73 0%,
    #1A5367 25%,
    #164555 50%,
    #123845 75%,
    #0E2C37 100%
  );
  padding: 80px 0;
  color: white;
  overflow: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
`;

const Tagline = styled.div`
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 16px;
  text-align: center;
`;

const Title = styled.h2`
  font-size: 64px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 64px;
  letter-spacing: -0.02em;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 48px;
  }
`;

const NavigationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 48px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -24px;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 1px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0) 100%
    );
  }
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

const NavigationItem = styled.button`
  background: none;
  border: none;
  text-align: left;
  padding: 16px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  
  ${props => props.$isActive && `
    &::after {
      content: '';
      position: absolute;
      bottom: -25px;
      left: 0;
      width: 100%;
      height: 2px;
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0.1) 0%,
        rgba(255, 255, 255, 0.8) 50%,
        rgba(255, 255, 255, 0.1) 100%
      );
      z-index: 1;
    }
  `}
  
  &:hover h3 {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const ItemTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
  color: ${props => props.$isActive ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
  transition: color 0.3s ease;
`;

const ItemDescription = styled.p`
  font-size: 14px;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  flex-grow: 1;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  border-radius: 24px;
  overflow: hidden;
  background: transparent;
  box-shadow: 
    0 30px 60px -15px rgba(0, 0, 0, 0.5),
    0 0 30px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.07);
  
  @media (max-width: 1024px) {
    border-radius: 24px 24px 0 0;
  }
`;

const StyledImage = styled(motion.img)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  padding: 0;
  background: transparent;
  transition: all 0.3s ease;

  ${ImageContainer}:hover & {
    transform: scale(1.02);
  }
`;

const NavigationArrows = styled.div`
  position: absolute;
  bottom: 24px;
  right: 24px;
  display: flex;
  gap: 12px;
  z-index: 2;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const InfoBar = styled.div`
  display: none;
  
  @media (max-width: 1024px) {
    display: flex;
    background: rgba(0, 0, 0, 0.95);
    padding: 24px;
    border-radius: 0 0 24px 24px;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;
  }
`;

const InfoContent = styled.div`
  flex: 1;
  padding-right: 72px; /* Space for the arrow */
`;

const InfoTitle = styled.h3`
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const InfoDescription = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.5;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 1.4;
  }
`;

const ArrowButton = styled.button`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  color: white;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MobileArrowButton = styled(ArrowButton)`
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  margin: 0;
  
  &:hover {
    transform: translateY(-50%) scale(1.05);
  }
`;

const MobileTitle = styled.div`
  position: absolute;
  top: 24px;
  left: 24px;
  z-index: 2;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  font-size: 32px;
  font-weight: 600;
  display: none;

  @media (max-width: 1024px) {
    display: block;
  }
`;

const CTAContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 64px;
`;

// Two-column responsive layout
const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  align-items: stretch;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

// Portrait variant of the image card, preserving the same look & feel
const PortraitCard = styled(ImageContainer)`
  padding-top: 135.6%;
`;

const LeftActions = styled.div`
  display: flex;
  justify-content: center;
`;

const RightIntro = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
`;

// Right headline and label
const RightTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 42px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: rgba(255, 255, 255, 0.96);

  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  margin: 8px 0 12px 0;
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);

  &::before,
  &::after {
    content: '';
    flex: 1 1 0%;
    height: 1px;
    background: linear-gradient(90deg, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0.0) 100%);
  }
`;

// Compact infographic list to enrich short content
const InfoStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  position: static;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.25);
`;

const InfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoHeading = styled.div`
  color: rgba(255,255,255,0.95);
  font-weight: 600;
  font-size: 15px;
  letter-spacing: -0.01em;
`;

/* Use existing InfoDescription (styled.p) defined above */

const RightActions = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;


const sections = ['identify', 'research', 'engage', 'personalize'];

// Add a helper function for smooth scrolling and message setting
const scrollToContact = (message) => {
  const contactSection = document.getElementById('contact-us');
  const messageInput = document.querySelector('#message');
  
  if (contactSection) {
    contactSection.scrollIntoView({ behavior: 'smooth' });
    
    setTimeout(() => {
      if (messageInput) {
        messageInput.focus();
        messageInput.value = message;
      }
    }, 1000);
  }
};

const PlatformSection = () => {
  const { t } = useLanguage();
  const handleSpeakWithClara = () => {
    window.open('http://clara.nexastaff.ai/', '_blank', 'noopener,noreferrer');
  };

  return (
    <Section id="in-actions">
      <Container>
        <Tagline>{t('quickStart.tagline')}</Tagline>
        <Title>{t('quickStart.title')}</Title>
        <TwoColumn>
          <LeftColumn>
            <PortraitCard>
              <StyledImage
                src="https://cdn.nexastaff.ai/00-assets/report.jpg"
                alt={t('quickStart.imageAlt')}
                loading="lazy"
                draggable={false}
              />
            </PortraitCard>
            {/* LeftActions removed per request */}
          </LeftColumn>

          <RightColumn>
            <RightTitle>{t('quickStart.rightTitle')}</RightTitle>

            <SectionLabel>{t('quickStart.label')}</SectionLabel>
            <InfoStrip>
              <InfoItem data-icon="MK">
                <InfoText>
                  <InfoHeading>{t('quickStart.items.kitMomentum.heading')}</InfoHeading>
                  <InfoDescription>{t('quickStart.items.kitMomentum.description')}</InfoDescription>
                </InfoText>
              </InfoItem>
              <InfoItem data-icon="RS">
                <InfoText>
                  <InfoHeading>{t('quickStart.items.aiBenefitScore.heading')}</InfoHeading>
                  <InfoDescription>{t('quickStart.items.aiBenefitScore.description')}</InfoDescription>
                </InfoText>
              </InfoItem>
              <InfoItem data-icon="BS">
                <InfoText>
                  <InfoHeading>{t('quickStart.items.businessSnapshot.heading')}</InfoHeading>
                  <InfoDescription>{t('quickStart.items.businessSnapshot.description')}</InfoDescription>
                </InfoText>
              </InfoItem>
            </InfoStrip>

            <RightActions>
              <RainbowButton onClick={handleSpeakWithClara}>{t('quickStart.cta')}</RainbowButton>
            </RightActions>
          </RightColumn>
        </TwoColumn>
      </Container>
    </Section>
  );
};

export default PlatformSection;